import React, { useMemo } from 'react';
import loadable from '@loadable/component';
import styled from 'libs/styled';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import { getLinkUrlFromLinkContent, type ReferencesMap } from '@fogia/core-web/utils/internalRefToSlug';
import type {
    HeadingLevel,
    HighlightedContentItemRaw,
    HighlightedContentItemTransformed,
    HighlightedContentModuleRaw,
    HighlightedContentPortrait,
} from './types';

const ModuleWrapper = styled('section')`
    margin: var(--module-margin-y) 0;
`;

/** Fallback so loadable never receives undefined (avoids hoistNonReactStatics "Cannot convert undefined or null to object"). */
const withDefault = <T,>(mod: { default?: T } | null | undefined): { default: T } => ({
    default: (mod?.default ?? (() => null)) as T,
});

const HighlightedContentDesktop = loadable(() => import('./HighlightedContentDesktop').then((mod) => withDefault(mod)));
const HighlightedContentMobile = loadable(() => import('./HighlightedContentMobile').then((mod) => withDefault(mod)));

const transformItem = (
    item: HighlightedContentItemRaw,
    references: ReferencesMap,
): HighlightedContentItemTransformed | null => {
    const portrait = item.media?.portrait;
    if (!portrait?.mediaType) {
        return null;
    }
    const hasMedia =
        (portrait.mediaType === 'image' && portrait.image) || (portrait.mediaType === 'video' && portrait.video);
    if (!hasMedia) {
        return null;
    }

    const primaryButton = item.buttons?.find((b) => b.type === 'buttonLinkComponent');
    const key = item.key ?? primaryButton?.label ?? item.title ?? 'highlighted-content-item';
    const href = primaryButton ? getLinkUrlFromLinkContent(primaryButton, references) : undefined;
    const target = primaryButton?.target;
    const label = primaryButton?.label;

    return {
        key,
        title: item.title,
        href,
        target,
        label,
        portrait: portrait as HighlightedContentPortrait,
        alt: (portrait as HighlightedContentPortrait).image?.alt,
    };
};

interface HighlightedContentModuleProps {
    data?: HighlightedContentModuleRaw;
}

const HighlightedContentModule: React.FC<HighlightedContentModuleProps> = (props) => {
    const { data: moduleData } = props;
    const references: ReferencesMap =
        (useAppSelector((state) => state.page?.data?.references) as ReferencesMap | undefined) ?? {};
    const isDesktop = useBreakpoint({ from: 'tablet.lg' });

    const transformedData = useMemo(() => {
        const raw: Partial<HighlightedContentModuleRaw> = moduleData ?? {};
        const rawItems = raw.items ?? [];
        const items = rawItems
            .map((item) => transformItem(item, references))
            .filter((item): item is HighlightedContentItemTransformed => item !== null);
        if (items.length === 0) return null;

        const primaryButton = raw.buttons?.find((b) => b.type === 'buttonLinkComponent');
        const headerSanityLink =
            primaryButton != null ? { sanityLink: primaryButton, label: primaryButton.label } : undefined;
        const titleObj = raw.title && typeof raw.title === 'object' ? raw.title : null;
        const titleText = titleObj ? titleObj.text : typeof raw.title === 'string' ? raw.title : undefined;
        const rawLevel = titleObj?.level;
        const titleLevel: HeadingLevel =
            typeof rawLevel === 'string' && /^h[1-6]$/i.test(rawLevel)
                ? (rawLevel.toLowerCase() as HeadingLevel)
                : 'h2';
        return {
            title: titleText,
            titleLevel,
            headerSanityLink,
            items,
        };
    }, [moduleData, references]);

    if (!transformedData) {
        return null;
    }

    if (isDesktop) {
        return (
            <ModuleWrapper>
                <HighlightedContentDesktop
                    title={transformedData.title}
                    titleLevel={transformedData.titleLevel}
                    headerSanityLink={transformedData.headerSanityLink}
                    items={transformedData.items}
                />
            </ModuleWrapper>
        );
    }

    return (
        <ModuleWrapper>
            <HighlightedContentMobile
                title={transformedData.title}
                titleLevel={transformedData.titleLevel}
                headerSanityLink={transformedData.headerSanityLink}
                items={transformedData.items}
            />
        </ModuleWrapper>
    );
};

export default HighlightedContentModule;
